import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CertificateModal from '../components/CertificateModal';
import { 
  Star, 
  Users, 
  BookOpen, 
  Award, 
  ArrowRight, 
  Play,
  Code,
  Database,
  Laptop,
  Brain,
  Target,
  CheckCircle,
  Phone,
  MapPin,
  Clock,
  TrendingUp,
  Search,
  Sparkles,
  Smartphone,
  Shield,
  Palette,
  Cpu,
  Globe,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import SuccessStories from '../components/SuccessStories';
import { allCourses } from '../data/courses';
import CourseCard from '../components/CourseCard';
import MentorSection from '../components/MentorSection';
import api from '../utils/axios';
import { getImageUrl, addCacheBuster } from '../config/apiConfig';
import { extractVideoId, createEmbedUrl, handleIframeError, getThumbnailUrl } from '../utils/youtubeUtils';
import YouTubeVideo from '../components/YouTubeVideo';
import rishiVyasImage from '../assets/Rishi_Vyas_DataAnalysis.jpg';
import dac1Image from '../assets/Dac-1.jpg';
import dac2Image from '../assets/Dac-2.jpg';

// Move CourseSection outside to prevent re-creation on every render
const CourseSection = React.memo(({ title, courses, viewAllLink }) => (
  <section className="section-padding-sm">
    <div className="container-max">
      <div className="flex items-center justify-between mb-8">
        <div className="flex-1">
          <h2 className="heading-tertiary">
            {title}
          </h2>
        </div>
        <div className="flex-shrink-0">
          <Link 
            to={viewAllLink} 
            className="btn btn-outline btn-sm inline-flex items-center px-6 py-3 rounded-xl hover:scale-105 transform transition-all duration-200"
          >
            View All
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>
      <div className="grid-responsive-4">
        {courses.map((course, index) => (
          <motion.div
            key={course.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <CourseCard course={course} />
          </motion.div>
        ))}
      </div>
    </div>
  </section>
));

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [couponCopied, setCouponCopied] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [videoModal, setVideoModal] = useState({ isOpen: false, videoUrl: '', studentName: '', company: '' });
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [currentCouponIndex, setCurrentCouponIndex] = useState(0);
  const [workshopModal, setWorkshopModal] = useState({ isOpen: false });
  const [workshopFormData, setWorkshopFormData] = useState({
    name: '',
    email: '',
    phone: '',
    workshop: '',
    message: ''
  });
  const [workshopStatus, setWorkshopStatus] = useState('idle');
  const [workshopErrorMessage, setWorkshopErrorMessage] = useState('');
  const [certificateModal, setCertificateModal] = useState({ isOpen: false });
  const [showAllVideos, setShowAllVideos] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 2);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Memoize all courses to prevent recreation
  const allCoursesFlat = useMemo(() => Object.values(allCourses).flat(), []);

  // Search logic with memoized courses
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    const filtered = allCoursesFlat.filter(course =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setSearchResults(filtered);
    setShowDropdown(true);
  }, [searchTerm, allCoursesFlat]);

  // Fetch active coupons
  useEffect(() => {
    api.get('/coupons/active')
      .then(res => {
        if (res.data.success && res.data.data && res.data.data.length > 0) {
          setActiveCoupons(res.data.data);
        } else {
          setActiveCoupons([]);
        }
      })
      .catch(err => {
        setActiveCoupons([]);
      });
  }, []);
  
  // Auto-slide coupons
  useEffect(() => {
    if (activeCoupons.length > 1) {
      const interval = setInterval(() => {
        setCurrentCouponIndex((prev) => (prev + 1) % activeCoupons.length);
      }, 5000); // Change coupon every 5 seconds
      return () => clearInterval(interval);
    }
  }, [activeCoupons]);

  // Coupon copy logic
  const handleCopyCoupon = useCallback(() => {
    const currentCoupon = activeCoupons[currentCouponIndex];
    if (currentCoupon && currentCoupon.code) {
      navigator.clipboard.writeText(currentCoupon.code);
      setCouponCopied(true);
      setTimeout(() => setCouponCopied(false), 2000);
    }
  }, [activeCoupons, currentCouponIndex]);

  // Search submit logic
  const handleSearch = useCallback((e) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      navigate(`/course/${searchResults[0].id}`);
      setShowDropdown(false);
    }
  }, [searchResults, navigate]);

  // Video modal functions
  const openVideoModal = useCallback((testimonial) => {
    if (testimonial.videoUrl !== "#") {
      // Convert YouTube URL to embeddable format
      const videoId = extractVideoId(testimonial.videoUrl);
      const embedUrl = videoId ? createEmbedUrl(videoId) : testimonial.videoUrl;
      
      setVideoModal({
        isOpen: true,
        videoUrl: embedUrl,
        studentName: testimonial.name,
        company: testimonial.company
      });
    }
  }, []);

  const closeVideoModal = useCallback(() => {
    setVideoModal({ isOpen: false, videoUrl: '', studentName: '', company: '' });
  }, []);

  // Certificate modal functions
  const openCertificateModal = useCallback(() => {
    setCertificateModal({ isOpen: true });
  }, []);

  const closeCertificateModal = useCallback(() => {
    setCertificateModal({ isOpen: false });
  }, []);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && videoModal.isOpen) {
        closeVideoModal();
      }
      if (e.key === 'Escape' && certificateModal.isOpen) {
        closeCertificateModal();
      }
    };

    if (videoModal.isOpen || certificateModal.isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [videoModal.isOpen, certificateModal.isOpen, closeVideoModal, closeCertificateModal]);



  // Memoize course sections to prevent re-creation
  const liveCourses = useMemo(() => {
    const allLive = allCourses.live;
              // Show specific live courses: Complete C++ with OOPS & STL first, then others (4 courses total)
    const curated = [];
    const completeCppCourse = allLive.find(course => course.slug === 'c-cpp-programming');
    if (completeCppCourse) curated.push(completeCppCourse);
    const cCourse = allLive.find(course => course.slug === 'c-for-beginners');
    if (cCourse) curated.push(cCourse);
    const cppCourse = allLive.find(course => course.slug === 'oops-cpp-live');
    if (cppCourse) curated.push(cppCourse);
    const javaCourse = allLive.find(course => course.slug === 'java-for-beginners-live');
    if (javaCourse) curated.push(javaCourse);
    return curated;
  }, []);

  const selfPacedCourses = useMemo(() => {
    const allSelfPaced = allCourses['self-paced'];
    // Show specific 5 courses in the requested order: C/C++/DSA Combo, C/C++ Foundation, Master Procedural & Object-Oriented Programming, Python for Beginners, Java for Beginners
    const curated = [];
    
    // Find specific courses by ID in the requested order
    const cCppDsaComboCourse = allSelfPaced.find(course => course.id === 'c-cpp-dsa-combo'); // C/C++/DSA Combo
    if (cCppDsaComboCourse) curated.push(cCppDsaComboCourse);
    
    const cCppFoundationCourse = allSelfPaced.find(course => course.id === 'c-cpp-foundation'); // C/C++ Foundation
    if (cCppFoundationCourse) curated.push(cCppFoundationCourse);
    
    // Note: "Master Procedural & Object-Oriented Programming" is the subtitle of C/C++ Foundation course
    // So we don't need a separate course for this as it's already included above
    
    const pythonForBeginnersCourse = allSelfPaced.find(course => course.id === 'python-for-beginners'); // Python for Beginners
    if (pythonForBeginnersCourse) curated.push(pythonForBeginnersCourse);
    
    const javaBeginnersCourse = allSelfPaced.find(course => course.id === 'java-for-beginners'); // Java for Beginners
    if (javaBeginnersCourse) curated.push(javaBeginnersCourse);
    
    return curated.slice(0, 4);
  }, []);

  const offlineCourses = useMemo(() => {
    const allOffline = allCourses.offline;
    // Show specific courses like offline category
    const curated = [];
    
    // First priority: C & C++ Complete Course (new combined course)
    const cCppCompleteCourse = allOffline.find(course => 
      course.id === 'oc17' || // C & C++ Complete Course
      course.slug === 'c-cpp-complete-offline'
    );
    if (cCppCompleteCourse) curated.push(cCppCompleteCourse);
    
    const mernCourse = allOffline.find(course => course.slug === 'mern-full-stack-offline');
    if (mernCourse) curated.push(mernCourse);
    
    const pythonDjangoCourse = allOffline.find(course => 
      course.id === 'oc2' || // Python + Django
      course.title.toLowerCase().includes('python + django')
    );
    if (pythonDjangoCourse) curated.push(pythonDjangoCourse);
    
    const javaCoreCourse = allOffline.find(course => 
      course.id === 'oc3' || // Java Core + Advance
      course.title.toLowerCase().includes('java core + advance')
    );
    if (javaCoreCourse) curated.push(javaCoreCourse);
    
    return curated.slice(0, 4);
  }, []);

  // Free Courses - Hidden on Home Page
  // const freeCourses = useMemo(() => {
  //   const allFree = allCourses.free;
  //   // Show specific courses like offline category
  //   const curated = [];
    
  //   // Find specific courses by ID or exact title match
  //   const javaCourse = allFree.find(course => 
  //     course.id === 'fc1' || // Java 8-17 New Features
  //     course.title.toLowerCase().includes('java 8-17 new features')
  //   );
  //   if (javaCourse) curated.push(javaCourse);
    
  //   const oopsCourse = allFree.find(course => 
  //     course.id === 'fc2' || // OOPS C++
  //     course.title.toLowerCase().includes('oops c++')
  //   );
  //   if (oopsCourse) curated.push(oopsCourse);
    
  //   const excelCourse = allFree.find(course => 
  //     course.id === 'fc3' || // Data Analysis Using MS Excel
  //     course.title.toLowerCase().includes('data analysis using ms excel')
  //   );
  //   if (excelCourse) curated.push(excelCourse);
    
  //   const mysqlCourse = allFree.find(course => 
  //     course.id === 'fc4' || // MySQL
  //     course.title.toLowerCase().includes('mysql')
  //   );
  //   if (mysqlCourse) curated.push(mysqlCourse);
    
  //   return curated.slice(0, 4);
  // }, []);

  // Function to get YouTube thumbnail URL
  const getYouTubeThumbnail = useCallback((videoUrl) => {
    if (videoUrl === "#") return null;
    const videoId = extractVideoId(videoUrl);
    return videoId ? getThumbnailUrl(videoId) : null;
  }, []);

  // Video testimonials data
  const videoTestimonials = useMemo(() => [
    {
      name: "Trupti Nagar Jain",
      company: "Microsoft, USA",
      videoUrl: "https://youtu.be/ZmID8vdF6c8?si=UU_e1K2dKWGfMAsK",
      thumbnail: getYouTubeThumbnail("https://youtu.be/ZmID8vdF6c8?si=UU_e1K2dKWGfMAsK")
    },
    {
      name: "Sachin Tomar",
      company: "City Bank, Pune",
      videoUrl: "https://www.youtube.com/watch?v=EZROGHX2TCY",
      thumbnail: getYouTubeThumbnail("https://www.youtube.com/watch?v=EZROGHX2TCY")
    },
      {
      name: "Shubham Jain",
      company: "NTT Data, USA",
      videoUrl: "https://youtu.be/OYIiAdhd-Ec?si=0npSzWhmBihZRjul",
      thumbnail: getYouTubeThumbnail("https://youtu.be/OYIiAdhd-Ec?si=0npSzWhmBihZRjul")
    },
  
    {
      name: "Neeraj Sharma",
      company: "HCL, Netherland",
      videoUrl: "https://youtu.be/q0i7v0ZjmBA?si=_xbZ592JX8kZcZvT",
      thumbnail: getYouTubeThumbnail("https://youtu.be/q0i7v0ZjmBA?si=_xbZ592JX8kZcZvT")
    },
    {
      name: "Anshul Gupta",
      company: "Wipro, Mumbai",
      videoUrl: "https://www.youtube.com/watch?v=pEvczGfvs9k&t=1s",
      thumbnail: getYouTubeThumbnail("https://www.youtube.com/watch?v=pEvczGfvs9k&t=1s")
    },
    {
      name: "Rakshit Bhatia",
      company: "JD Software, Banglore",
      videoUrl: "https://youtu.be/TAHnPRA-ghA?si=3Tndt4e2WxkyIAbx",
      thumbnail: getYouTubeThumbnail("https://youtu.be/TAHnPRA-ghA?si=3Tndt4e2WxkyIAbx")
    },
    {
      name: "Shiva Jain",
      company: "TCS, Indore",
      videoUrl: "https://youtu.be/CiXi9FTXnnA?si=fJhDAeqCfTJmAuCA",
      thumbnail: getYouTubeThumbnail("https://youtu.be/CiXi9FTXnnA?si=fJhDAeqCfTJmAuCA")
    },
  ], [getYouTubeThumbnail]);

  // YouTube Comment Testimonials
  const youtubeTestimonials = useMemo(() => [
    {
      name: "Shashwat Bajpai",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Sir you are an excellent teacher. Can we expect a DSA in java full course for free by you? Or is it too much to ask",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Shashwat Bajpai",
      role: "Student", 
      source: "YouTube Comment",
      testimonial: "Amazing lecture sir. Far better than so called paid courses. Sir you are covering almost everything. This actually is what they call 'Quality Content'. Shocked with the number of views though",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Ahmad Saleem",
      role: "Student",
      source: "YouTube Comment", 
      testimonial: "Thanks alot Sir g. If greatness had a face it will definitely be like you 🥰🥰🥰",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Blog.vestor",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Sir, your method of teaching is much better than others. Due to which we do not have any problem in understanding the concept.",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Shivanshi Singh",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Thank you so much sir for providing us with such detailed concepts and making it easy to understand. I Understood everything from basic and feel happy to learn",
      rating: 5,
      avatar: "👩‍🎓"
    },
    {
      name: "Vyankatesh Birajdar",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "sir i like your videos you cover all important topic in detail and guide how to prepare it for interview",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Asiis",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "coders boht achay hon gay youtube pe but this person is the best coding teacher. teaching is a different paradigm from coding.....",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Manish Pant",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Never seen such a great video on exception handling. Thank a lot",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Vinod Sharma",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "So much value in this channel. I hope more people find and use it.",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "FatherOfAbrahamicReligio",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "You are the God Of Java",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "FatherOfAbrahamicReligio",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Amazing lecture ❤️ sir, keep on doing your. You have a unique style of teaching that no one has.",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Shiralijain9591",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Nested query explained in best possible way. Know the actual difference between joins and nesting. Amazing lecture sir 👌",
      rating: 5,
      avatar: "👩‍🎓"
    },
    {
      name: "Memo8435",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Amazing 😍😍",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Pixelbihari",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "bilkul sahi kaha sir aapne (You said it absolutely right, sir)",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Filmreactionbyakk6909",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Thanks sir aapki bajah se coding me confidence aaya h (Thanks sir, because of you I gained confidence in coding)",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Filmreactionbyakk6",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Functions ab mujhe ache se smjh me aata hai apki wajah se sir. Big respect to bajpai sir ❤️❤️",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Rohitsinghkushwaha3",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Feeling very confident after making all programs on vs code with absolutely ZERO error. Thankyou Sir #CodingKranti@Bajpai Sir",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Bhaweshjoshi6705",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Unbeatable king in the programming languages......thank you sir for making programming as a 'baccho ka khel' (child's play)",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Rohitsinghkushwaha332",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "Superb video for beginners ...Thanks Sir",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "SakshiSharma-xp9lt",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "The lectures are so detailed even a student with a non IT background can understand.",
      rating: 5,
      avatar: "👩‍🎓"
    },
    {
      name: "Agnostic97",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "mai pichhle 3 saal se coding ke basic sikhne ki koshish kr raha tha but concept clear nahi ho pa raha tha, aaj OSOP coding channel pe smjh aya. Itna basic and simple concept aaj tak kahi nahi dekha. Thanks a lot OSOP coding, u really made my day. 🙏🙏🙏😊😊😊",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Bhavna2320",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "If you are here.. Congratulations 🎉🎊🥳 you have amazing content of coding with great personality as bajpai sir 🙏🙏",
      rating: 5,
      avatar: "👩‍🎓"
    },
    {
      name: "Shashwatbajpai8989",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "So good No one explained genric better than this on YouTube",
      rating: 5,
      avatar: "👨‍🎓"
    },
    {
      name: "Officaildiamondfact",
      role: "Student",
      source: "YouTube Comment",
      testimonial: "This is right platform for as a beginners",
      rating: 5,
      avatar: "👨‍🎓"
    }
  ], []);

  const [currentPromoSlide, setCurrentPromoSlide] = useState(0);
  const [promotionalSlides, setPromotionalSlides] = useState([]);
  const [slidesLoading, setSlidesLoading] = useState(true);

  // Fetch promotional slides from API
  useEffect(() => {
    const fetchPromotionalSlides = async () => {
      try {
        setSlidesLoading(true);
        const response = await fetch(addCacheBuster('https://osopcoding.com/api/promotional-slides/active'));
        const data = await response.json();
        
        if (data.success && data.data) {
          // Transform API data to match the expected format
          const transformedSlides = data.data.map(slide => ({
            title: slide.title,
            subtitle: slide.subtitle,
            description: slide.description,
            bgColor: slide.bg_color,
            icon: getIconComponent(slide.icon_name),
            link: slide.link,
            image_url: slide.image_url
          }));
          setPromotionalSlides(transformedSlides);
        } else {
          // Fallback to default slides if API fails
          setPromotionalSlides(defaultPromotionalSlides);
        }
      } catch (error) {
        // Fallback to default slides
        setPromotionalSlides(defaultPromotionalSlides);
      } finally {
        setSlidesLoading(false);
      }
    };

    fetchPromotionalSlides();
  }, []);

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const iconMap = {
      'Code': <Code className="w-8 h-8" />,
      'Brain': <Brain className="w-8 h-8" />,
      'Database': <Database className="w-8 h-8" />,
      'Laptop': <Laptop className="w-8 h-8" />,
      'Target': <Target className="w-8 h-8" />,
      'TrendingUp': <TrendingUp className="w-8 h-8" />,
      'Shield': <Shield className="w-8 h-8" />,
      'Palette': <Palette className="w-8 h-8" />,
      'Cpu': <Cpu className="w-8 h-8" />,
      'Smartphone': <Smartphone className="w-8 h-8" />,
      'CheckCircle': <CheckCircle className="w-8 h-8" />,
      'Users': <Users className="w-8 h-8" />,
      'BookOpen': <BookOpen className="w-8 h-8" />,
      'Award': <Award className="w-8 h-8" />,
      'Globe': <Globe className="w-8 h-8" />
    };
    return iconMap[iconName] || <Code className="w-8 h-8" />;
  };

  // Helper function to get proper image URL
  // Using getImageUrl from apiConfig

  // Default promotional slides as fallback
  const defaultPromotionalSlides = useMemo(() => [
    {
      title: "Java for Beginners",
      subtitle: "Self-Paced Java Mastery",
      description: "Start from scratch and master Java programming at your own pace.",
      bgColor: "from-blue-600 to-blue-800",
      icon: <Code className="w-8 h-8" />,
      link: "/courses/self-paced/java-for-beginners"
    },
    {
      title: "OOPs in C++",
      subtitle: "Self-Paced C++ OOPs Mastery",
      description: "Learn Object-Oriented Programming in C++ with hands-on projects.",
      bgColor: "from-blue-700 to-indigo-600",
      icon: <Code className="w-8 h-8" />,
      link: "/courses/self-paced/oops-cpp"
    }
  ], []);

  useEffect(() => {
    if (promotionalSlides.length > 0) {
      const interval = setInterval(() => {
        setCurrentPromoSlide((prev) => (prev + 1) % promotionalSlides.length);
      }, 5000); // Changed to 5 seconds for better readability
      return () => clearInterval(interval);
    }
  }, [promotionalSlides.length]);

  const categoryLabels = [
    { name: 'All', icon: '🔥', href: '/courses/category/all' },
    { name: 'Certification Courses', icon: '🏆', href: '/courses/category/certification' },
    { name: 'DSA / Placements', icon: '💼', href: '/courses/category/dsa-placements' },
    { name: 'Development / Testing', icon: '💻', href: '/courses/category/development' },
    { name: 'Machine Learning / Data Science', icon: '🤖', href: '/courses/category/ml-ds' },
    { name: 'Programming Languages', icon: '⚡', href: '/courses/category/programming' },
    { name: 'Cloud / DevOps', icon: '☁️', href: '/courses/category/cloud-devops' },
    { name: 'GATE', icon: '🎯', href: '/courses/category/gate' }
  ];

  const handleWorkshopChange = (e) => {
    const { name, value } = e.target;
    setWorkshopFormData(prev => ({ ...prev, [name]: value }));
    
    if (workshopStatus === 'error') {
      setWorkshopStatus('idle');
      setWorkshopErrorMessage('');
    }
  };

  const handleWorkshopSubmit = async (e) => {
    e.preventDefault();
    setWorkshopStatus('loading');
    setWorkshopErrorMessage('');
    
    try {
      await api.post('/workshop-registrations', workshopFormData);
      setWorkshopStatus('success');
      setTimeout(() => {
        setWorkshopStatus('idle');
        setWorkshopFormData({
          name: '',
          email: '',
          phone: '',
          workshop: '',
          message: ''
        });
        setWorkshopModal({ isOpen: false });
      }, 3000);
    } catch (err) {
      if (err.response) {
        const errorData = err.response.data;
        if (errorData.message) {
          setWorkshopErrorMessage(errorData.message);
        } else if (err.response.status === 400) {
          setWorkshopErrorMessage('Please check your form data and try again.');
        } else if (err.response.status === 500) {
          setWorkshopErrorMessage('Server error. Please try again later.');
        } else {
          setWorkshopErrorMessage('Something went wrong. Please try again.');
        }
      } else if (err.request) {
        setWorkshopErrorMessage('Network error. Please check your internet connection and try again.');
      } else {
        setWorkshopErrorMessage('An unexpected error occurred. Please try again.');
      }
      
      setWorkshopStatus('error');
    }
  };

  const openWorkshopModal = () => {
    setWorkshopModal({ isOpen: true });
  };

  const closeWorkshopModal = () => {
    setWorkshopModal({ isOpen: false });
    setWorkshopStatus('idle');
    setWorkshopErrorMessage('');
  };



  return (
    <div className="homepage-container min-h-screen bg-gray-50 dark:bg-gray-900 theme-transition prevent-overflow">
      {/* Hero Section with Promotional Banners */}
      <section className="bg-gradient-professional relative">
        <div className="absolute inset-0 bg-dots-dark opacity-30"></div>
        <div className="relative z-10 pt-24 sm:pt-32 lg:pt-40 pb-12 sm:pb-16 lg:pb-24">
          <div className="container-max relative px-3 sm:px-4 lg:px-6">
            {/* Promotional Cards Section */}
            <div className={`grid grid-cols-1 gap-2 sm:gap-3 lg:gap-4 ${activeCoupons.length > 0 ? 'md:grid-cols-2 lg:grid-cols-3' : 'lg:grid-cols-1'}`}>
              {/* Coupon Card - Only show if active coupons exist */}
              {activeCoupons.length > 0 && (
                <motion.div
                  className="bg-white/10 backdrop-blur-xl rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 text-white shadow-2xl border border-white/20 flex flex-col items-center justify-center w-full relative overflow-hidden min-h-[280px] sm:min-h-[320px]"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  {/* Coupon Slides */}
                  {activeCoupons.map((coupon, index) => {
                    const currentCoupon = activeCoupons[currentCouponIndex];
                    return (
                      <motion.div
                        key={coupon.id}
                        className={`absolute inset-0 p-2 sm:p-3 lg:p-4 flex flex-col items-center justify-center ${
                          index === currentCouponIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                        }`}
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ 
                          opacity: index === currentCouponIndex ? 1 : 0,
                          x: index === currentCouponIndex ? 0 : 100
                        }}
                        transition={{ duration: 0.5 }}
                      >
                        <div className="flex flex-row items-center justify-center mb-1.5 gap-1">
                          <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold animate-pulse shadow-lg whitespace-nowrap">
                            {currentCoupon.discount_percentage ? `${currentCoupon.discount_percentage}% OFF` : 'SPECIAL OFFER'}
                          </span>
                          <span className="text-[8px] sm:text-xs uppercase tracking-wider font-semibold bg-white/20 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                            {currentCoupon.category ? currentCoupon.category.toUpperCase() : 'LIMITED TIME'}
                          </span>
                        </div>
                        <h2 className="text-xs sm:text-sm font-bold mb-1 text-center">COUPON CODE:</h2>
                        <div className="flex flex-col items-center mb-1.5 w-full max-w-[200px] sm:max-w-xs">
                          <div className="bg-white/95 backdrop-blur-sm rounded-lg p-1 sm:p-1.5 mb-1.5 border-2 border-dashed border-yellow-300 shadow-2xl hover:shadow-glow transition-all duration-300 group w-full">
                            <div className="text-center">
                              <div className="text-lg sm:text-xl lg:text-2xl font-black text-primary-800 tracking-wider mb-0.5 sm:mb-1 group-hover:scale-105 transition-transform duration-200">
                                {currentCoupon.code}
                              </div>
                              <div className="text-[8px] sm:text-[10px] lg:text-xs text-primary-600 font-medium uppercase tracking-wide">
                               {currentCoupon.category ? `${currentCoupon.category} Courses` : 'All Self-Paced Courses'}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={handleCopyCoupon}
                            className="bg-green-600 hover:bg-green-700 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg font-bold text-[10px] sm:text-xs transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-1 sm:space-x-2 w-full max-w-[140px] sm:max-w-[160px] justify-center"
                            aria-label="Copy coupon code"
                          >
                            <span role='img' aria-label='copy'>📋</span> Copy Code
                          </button>
                        </div>
                        {couponCopied && (
                          <div className="text-center">
                            <div className="inline-flex items-center bg-green-500/20 text-green-200 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-[10px] lg:text-xs font-semibold backdrop-blur-sm border border-green-400/30">
                              ✅ Coupon copied successfully!
                            </div>
                          </div>
                        )}
                        <p className="text-[8px] sm:text-[10px] lg:text-xs opacity-90 text-center mt-1.5 sm:mt-2 font-medium">
                          {currentCoupon.description || 'Special offer for our courses'}
                        </p>
                        {currentCoupon.valid_until && (
                          <p className="text-[6px] sm:text-[8px] lg:text-[10px] opacity-70 text-center mt-0.5 sm:mt-1 font-medium">
                            Valid until: {new Date(currentCoupon.valid_until).toLocaleDateString()}
                          </p>
                        )}
                      </motion.div>
                    );
                  })}
                  
                  {/* Coupon Navigation Dots */}
                  {activeCoupons.length > 1 && (
                    <div className="absolute bottom-1 sm:bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-0.5 sm:space-x-1">
                      {activeCoupons.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentCouponIndex(index)}
                          className={`w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full transition-all duration-200 ${
                            index === currentCouponIndex ? 'bg-white' : 'bg-white/30'
                          }`}
                          aria-label={`Go to coupon ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
              
              {/* Promotional Slider */}
            <motion.div
                className={`bg-white/10 backdrop-blur-xl rounded-lg sm:rounded-xl p-2 sm:p-3 lg:p-4 text-white shadow-2xl border border-white/20 ${activeCoupons.length > 0 ? 'md:col-span-2 lg:col-span-2' : 'lg:col-span-1'} flex flex-col justify-center w-full relative overflow-hidden min-h-[280px] sm:min-h-[320px]`}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
                {promotionalSlides.map((slide, index) => (
                  <motion.div
                    key={slide.title}
                    className={`absolute inset-0 p-2 sm:p-3 lg:p-4 flex flex-col justify-center ${
                      index === currentPromoSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ 
                      opacity: index === currentPromoSlide ? 1 : 0,
                      x: index === currentPromoSlide ? 0 : 100
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    {/* Pure Image Display */}
                    {slide.image_url ? (
                      <div className="absolute inset-0">
                        <img 
                          src={getImageUrl(slide.image_url)} 
                          alt={slide.title}
                          className="w-full h-full object-contain rounded-lg"
                          onLoad={() => {
                            // Image loaded successfully
                          }}
                          onError={(e) => {
                            // Safely handle the error without accessing potentially invalid DOM nodes
                            if (e && e.target && e.target.style) {
                              e.target.style.display = 'none';
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div className={`absolute inset-0 bg-gradient-to-r ${slide.bgColor} opacity-90 rounded-lg`} />
                    )}
                    
                    {/* Optional Link Overlay */}
                    {slide.link && (
                      <Link 
                        to={slide.link}
                        className="absolute inset-0 z-10 block"
                        aria-label={`Go to ${slide.title}`}
                      />
                    )}
                  </motion.div>
                ))}
                
                {/* Slide Indicators */}
                <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1.5 sm:space-x-2 z-20">
                  {promotionalSlides.map((_, index) => (
                    <button
                      key={index}
                      className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                        index === currentPromoSlide 
                          ? 'bg-white w-4 sm:w-6' 
                          : 'bg-white/50 hover:bg-white/70 w-1.5 sm:w-2'
                      }`}
                      onClick={() => setCurrentPromoSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </motion.div>
            </div>
            
            {/* Book Free Demo Section */}
          <motion.div
              className="mt-4 sm:mt-6 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Book Free Demo - Left Side */}
                <div className="inline-flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 bg-white/10 backdrop-blur-xl rounded-lg sm:rounded-xl p-3 sm:p-5 border border-white/20 shadow-xl w-full">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-primary-500/20 p-2 sm:p-2.5 rounded-lg">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-primary-400" />
                          </div>
                  <div className="text-left">
                    <h3 className="text-white font-semibold text-base sm:text-lg">Book Free Demo Class</h3>
                    <p className="text-white/80 text-xs sm:text-sm">Get personalized guidance from our experts</p>
                    </div>
                </div>
                <Link
                  to="/enquire"
                  className="inline-flex items-center bg-primary-500 hover:bg-primary-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 group shadow-lg hover:shadow-xl whitespace-nowrap text-sm sm:text-base w-full sm:w-auto justify-center"
                >
                  Schedule Now
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

                {/* Workshop Register - Right Side */}
                <div className="inline-flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 bg-white/10 backdrop-blur-xl rounded-lg sm:rounded-xl p-3 sm:p-5 border border-white/20 shadow-xl w-full">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="bg-green-500/20 p-2 sm:p-2.5 rounded-lg">
                      <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-white font-semibold text-base sm:text-lg">Register for Workshop</h3>
                      <p className="text-white/80 text-xs sm:text-sm">Join our upcoming workshops and masterclasses</p>
                    </div>
                  </div>
                  <button
                    onClick={openWorkshopModal}
                    className="inline-flex items-center bg-green-500 hover:bg-green-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 group shadow-lg hover:shadow-xl whitespace-nowrap text-sm sm:text-base w-full sm:w-auto justify-center"
                  >
                    Register Now
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mentor Section */}
      <MentorSection />

      {/* Main Content Area */}
      <div className="w-full dark:bg-gray-900">
        {/* Learning Path Section */}
        <section className="py-8 sm:py-10 lg:py-12 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
            {/* Section Header */}
            <motion.div 
              className="text-center mb-6 sm:mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                Choose from Premium Coding Courses to Enhance Your Skills
              </h2>
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Whether you're just starting your journey or looking to advance your career, we have the perfect courses for you
              </p>
            </motion.div>

            {/* Course Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
              {/* First Year Job-Ready Course */}
              <motion.div 
                className="group relative overflow-hidden rounded-xl shadow-lg p-4 sm:p-5 flex flex-col justify-between min-h-[240px] hover:shadow-xl hover:scale-102 transition-all duration-300"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                whileHover={{ y: -3 }}
                            >
                <div className="relative z-10">
                  {/* Foundation Course Image */}
                  <div className="relative w-full h-80 group">
                    <img 
                      src={dac1Image} 
                      alt="Foundation Course (4 Years)"
                      className="w-full h-full rounded-lg shadow-lg object-contain"
                      style={{ minHeight: '320px' }}
                    />
                    {/* Hover Overlay with Button */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                      <Link 
                        to="/course/OSOPFoundation"
                        className="bg-white/90 text-blue-600 hover:bg-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 text-sm group-hover:scale-105 transform"
                      >
                        <span>View Course Details</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Pass Out Students Course */}
              <motion.div 
                className="group relative overflow-hidden rounded-xl shadow-lg p-4 sm:p-5 flex flex-col justify-between min-h-[240px] hover:shadow-xl hover:scale-102 transition-all duration-300"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                whileHover={{ y: -3 }}
                            >
                <div className="relative z-10">
                  {/* Target Course Image */}
                  <div className="relative w-full h-80 group">
                    <img 
                      src={dac2Image} 
                      alt="Target Course (6 Months - 1 Year)"
                      className="w-full h-full rounded-lg shadow-lg object-contain"
                      style={{ minHeight: '320px' }}
                    />
                    {/* Hover Overlay with Button */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                      <Link 
                        to="/course/Target"
                        className="bg-white/90 text-green-600 hover:bg-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 text-sm group-hover:scale-105 transform"
                      >
                        <span>View Course Details</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Search Section - Moved here */}
           
          </div>
        </section>

        {/* Live Courses */}
        <CourseSection 
          title="Live Courses" 
          courses={liveCourses} 
          viewAllLink="/courses/live" 
        />

        {/* Self-Paced Courses */}
        <CourseSection 
          title="Self-Paced Courses" 
          courses={selfPacedCourses} 
          viewAllLink="/courses/self-paced" 
        />

        {/* Offline Classes */}
        <CourseSection 
          title="Offline Classes" 
          courses={offlineCourses} 
          viewAllLink="/courses/offline" 
        />

        {/* Free Courses - Hidden on Home Page */}
        {/* <CourseSection 
            title="Free Courses" 
            courses={freeCourses} 
            viewAllLink="/courses/free" 
          /> */}




      {/* Real Feedback Section */}
      <section className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/10 dark:to-primary-800/10 py-20 rounded-3xl">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Success Stories
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            How OSOPCoding Helped Students in Cracking their Dream IT MNC
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videoTestimonials.slice(0, showAllVideos ? videoTestimonials.length : 4).map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 h-full flex flex-col cursor-pointer"
                onClick={() => openVideoModal(testimonial)}
              >
                <div className="relative aspect-video bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 flex-shrink-0 overflow-hidden">
                  {/* Video Thumbnail */}
                  {testimonial.thumbnail ? (
                    <img 
                      src={testimonial.thumbnail} 
                      alt={`${testimonial.name} testimonial thumbnail`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to gradient background if thumbnail fails to load
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-primary-600/20 rounded-full flex items-center justify-center mb-2 mx-auto">
                          <Play className="w-6 h-6 text-primary-600" />
                        </div>
                        <p className="text-sm text-primary-600 font-medium">Video Coming Soon</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="w-16 h-16 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center transition-all duration-200 group-hover:scale-110 shadow-2xl">
                      <Play className="w-6 h-6 text-white ml-1" />
                    </div>
                  </div>
                  
                  {/* Duration overlay */}
                  {testimonial.videoUrl !== "#" && (
                    <div className="absolute bottom-3 right-3 bg-black/80 text-white px-2 py-1 rounded text-xs font-medium">
                      Video
                    </div>
                  )}
                </div>
                <div className="p-4 flex-grow flex flex-col justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1 line-clamp-1">
                      {testimonial.name}
                    </h3>
                    <p className="text-primary-600 dark:text-primary-400 text-sm font-medium mb-3 line-clamp-1">
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Show All Button */}
          {videoTestimonials.length > 4 && (
            <motion.div 
              className="text-center mt-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <button
                onClick={() => setShowAllVideos(!showAllVideos)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
              >
                {showAllVideos ? (
                  <>
                    <span>Show Less</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </>
                ) : (
                  <>
                    <span>Show All Videos</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </>
                )}
              </button>
            </motion.div>
          )}
        </div>
      </section>

    {/* Success Stories */}
                <div className="bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 rounded-3xl shadow-2xl border border-green-100 dark:border-gray-700 p-8 lg:p-12 relative overflow-hidden">
                  {/* Background decorative elements */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/10 to-emerald-400/10 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-teal-400/10 to-cyan-400/10 rounded-full blur-2xl"></div>
                  
                  <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-8 relative z-10"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl mb-6 shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4">
                   Our Alumni
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
                      Real stories from our graduates who transformed their careers with OSOP
                    </p>
                  </motion.div>

                  {/* Success Stories Grid */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mb-8"
                  >
                    <SuccessStories />
                  </motion.div>

                  {/* Call to action */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="text-center"
                  >
                    <div 
                      onClick={() => navigate('/placement')}
                      className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer group"
                    >
                      <span>View More Alumni</span>
                      <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                      Discover more inspiring career transformations
                    </p>
                  </motion.div>
                </div>

{/* Our Alumni are Placed in */}
                <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 rounded-3xl shadow-2xl border border-blue-100 dark:border-gray-700 p-8 lg:p-12 relative overflow-hidden">
                  {/* Background decorative elements */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-indigo-400/10 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-400/10 to-pink-400/10 rounded-full blur-2xl"></div>
                  
                  <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-12 relative z-10"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6z" />
                      </svg>
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4">
                      Our Alumni are Placed in
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
                      Top companies worldwide trust OSOP graduates with their most critical projects
                    </p>
                  </motion.div>

                  {/* Main Image with enhanced styling */}
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.4 }}
                    className="relative"
                  >
                    <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-2xl border border-gray-100 dark:border-gray-700">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-3xl"></div>
                      <img 
                        src={require('../assets/hiring-partners-grid.jpg')} 
                        alt="Hiring Partners Grid" 
                        className="w-full h-auto rounded-2xl shadow-lg relative z-10 hover:scale-105 transition-transform duration-500"
                      />
                      {/* Overlay with company logos animation */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl pointer-events-none"></div>
                    </div>
                  </motion.div>

                  {/* Call to action */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="text-center mt-8"
                  >
                    <button
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                      onClick={() => navigate('/placement')}
                    >
                      View Success Stories
                    </button>
                  </motion.div>
                </div>
        {/* Certification Section */}
        <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/10 dark:to-indigo-900/10">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
              {/* Left Side - Image */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 transform rotate-1 sm:rotate-2 hover:rotate-0 transition-transform duration-500">
                  {/* Rishi Vyas Data Analysis Image */}
                  <div className="relative overflow-hidden rounded-xl sm:rounded-2xl cursor-pointer group" onClick={openCertificateModal}>
                    <img 
                      src={rishiVyasImage} 
                      alt="Rishi Vyas Data Analysis"
                      className="w-full h-auto object-cover rounded-xl sm:rounded-2xl shadow-lg transition-transform duration-300 group-hover:scale-105"
                      onLoad={() => {
                        // Image loaded successfully
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Zoom overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-xl sm:rounded-2xl flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full animate-bounce"></div>
                <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-blue-400 rounded-full animate-pulse"></div>
                <div className="absolute top-1/2 -right-6 w-4 h-4 bg-green-400 rounded-full animate-ping"></div>
              </motion.div>

              {/* Right Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-6"
              >
                <div>
                  <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-full text-sm font-medium">
                    🏆 Get Certified 
                  </span>
                </div>

                <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
                  OSOP Certified IT Professional
                </h2>

                <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                  Get industry recognized Certificate on completion of courses. Certified from OSOP Means the "Guarantee of Skills".
                </p>

               

                {/* OSOP Coding Certificate Features */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 shadow-lg border border-blue-200 dark:border-blue-800">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <Award className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-2" />
                    OSOP Coding Certificates
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Industry-Recognized</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Certificates recognized by IT companies worldwide</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Project-Based Learning</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Real projects included with every certification course</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">26+ Years Excellence</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Proven track record of 15K+ IT Professional trained in classroom</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="pt-4">
                  <Link 
                    to="/enquire"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 inline-flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <span>Get Certified Today</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 px-4 py-2 rounded-full text-sm font-medium mb-4 inline-block">
                ❓ Got Questions?
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
                Find answers to common questions about OSOP courses, admissions, and career opportunities
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto">
              <div className="grid gap-6">
                {[
                  {
                    question: "What makes OSOP different from other coding institutes?",
                    answer: "OSOP stands out with 26+ years of experience, personalized attention with small batch sizes, 100% job placement assistance, and industry-relevant curriculum designed by experts. Our proven track record of 15K+ successful students speaks for itself."
                  },
                  {
                    question: "Do you provide job placement assistance?",
                    answer: "Yes! We provide comprehensive job placement assistance including resume building, interview preparation, mock interviews, and direct connections with 250+ hiring partner companies. Our placement team works closely with students until they secure their dream job."
                  },
                  {
                    question: "What are the course duration and timings?",
                    answer: "Course durations vary from 3 months to 2 years depending on the program. We offer flexible timings including morning, evening, and weekend batches to accommodate working professionals and students. Online and offline both options are available."
                  },
                  {
                    question: "What are the prerequisites for joining OSOP courses?",
                    answer: "Most of our courses are designed for beginners with no prior programming experience. For advanced courses, basic computer knowledge is sufficient. We offer foundation courses for complete beginners and specialized tracks for experienced professionals."
                  },
                  {
                    question: "Do you provide certificates upon course completion?",
                    answer: "Yes, we provide industry-recognized certificates upon successful completion of courses. Our certificates are valued by employers and include project portfolios that showcase your practical skills to potential employers."
                  },
                  {
                    question: "What is the fee structure and payment options?",
                    answer: "We offer competitive pricing with flexible payment options including EMI facilities. Fee varies by course (₹8,999 to ₹149,999). We also provide scholarships for deserving students and early bird discounts. Contact us for detailed fee structure."
                  },
                  {
                    question: "Can I switch between online and offline modes?",
                    answer: "Yes, we offer hybrid learning options. You can start with online classes and switch to offline when convenient, or vice versa. This flexibility ensures you never miss classes due to personal or professional commitments."
                  },
                  {
                    question: "What kind of projects will I work on?",
                    answer: "You'll work on real-world industry projects including web applications, mobile apps, data science projects, and enterprise solutions. Projects are designed to build your portfolio and give hands-on experience with current technologies used in the industry."
                  }
                ].map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
                  >
                    <details className="group">
                      <summary className="flex items-center justify-between p-4 sm:p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white pr-2 sm:pr-4 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200">
                          {faq.question}
                        </h3>
                        <div className="flex-shrink-0 w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center group-open:rotate-180 transition-transform duration-200">
                          <svg 
                            className="w-5 h-5 text-primary-600 dark:text-primary-400 transform group-open:rotate-180 transition-transform duration-200" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      <div className="px-4 pb-4 sm:px-6 sm:pb-6">
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 sm:pt-4">
                          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </details>
                  </motion.div>
                ))}
              </div>

              {/* Contact CTA */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-center mt-12 p-8 bg-gradient-to-r from-primary-600 to-primary-700 rounded-3xl text-white"
              >
                <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
                <p className="text-primary-100 mb-6 text-lg">
                  Our admission counselors are here to help you choose the right course for your career goals.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/enquire"
                    className="bg-white text-primary-600 hover:bg-gray-100 font-bold py-3 px-8 rounded-xl transition-all duration-300 inline-flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <span>Get Free Counseling</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <a
                    href="tel:+917649098000"
                    className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 font-bold py-3 px-8 rounded-xl transition-all duration-300 inline-flex items-center justify-center space-x-2 transform hover:scale-105"
                  >
                    <Phone className="w-5 h-5" />
                    <span>Call Now</span>
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-r from-primary-600 to-primary-800">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-8 text-center text-white">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="w-full sm:w-1/3"
              >
                <div className="text-3xl sm:text-4xl font-bold mb-2">26+</div>
                <div className="text-primary-200 text-sm sm:text-base">Years Of Training & Consulting</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="w-full sm:w-1/3"
              >
                <div className="text-3xl sm:text-4xl font-bold mb-2">15K+</div>
                <div className="text-primary-200 text-sm sm:text-base">Students Trained</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="w-full sm:w-1/3"
              >
                <div className="text-3xl sm:text-4xl font-bold mb-2">100%</div>
                <div className="text-primary-200 text-sm sm:text-base">Satisfied Learners</div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>

      {/* Video Modal */}
      {videoModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10010] flex items-center justify-center p-2 sm:p-4" style={{ paddingTop: '3rem' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-gray-900 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl w-full h-full max-w-6xl max-h-[90vh] sm:max-h-[85vh] flex flex-col"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {videoModal.studentName}
                </h3>
                <p className="text-primary-600 dark:text-primary-400 font-medium text-sm sm:text-base">
                  {videoModal.company}
                </p>
              </div>
              <button
                onClick={closeVideoModal}
                className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors duration-200 flex-shrink-0"
                aria-label="Close video"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Video Container - Flexible height */}
            <div className="flex-1 bg-black min-h-0 flex items-center justify-center">
              <div className="w-full h-full flex items-center justify-center">
                <YouTubeVideo
                  videoUrl={videoModal.videoUrl}
                  title={`${videoModal.studentName} - ${videoModal.company} Testimonial`}
                  className="w-full h-full max-w-full max-h-full"
                  style={{ minHeight: '300px' }}
                  fallbackMessage={`Watch ${videoModal.studentName}'s testimonial on YouTube`}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Real feedback from our successful student
                </div>
                <button
                  onClick={closeVideoModal}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Workshop Registration Modal */}
      {workshopModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 pt-20 sm:pt-24 lg:pt-28">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg lg:max-w-xl mx-4 max-h-[75vh] sm:max-h-[80vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Workshop Registration</h3>
                <button
                  onClick={closeWorkshopModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {workshopStatus === 'success' ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-green-600 dark:text-green-400 mb-2">Registration Successful!</h4>
                  <p className="text-gray-600 dark:text-gray-400">Thank you for registering. We'll contact you soon with workshop details!</p>
                </div>
              ) : (
                <form onSubmit={handleWorkshopSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={workshopFormData.name}
                      onChange={handleWorkshopChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600 text-gray-900 dark:text-white"
                      placeholder="Your Name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={workshopFormData.email}
                      onChange={handleWorkshopChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600 text-gray-900 dark:text-white"
                      placeholder="you@email.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={workshopFormData.phone}
                      onChange={handleWorkshopChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600 text-gray-900 dark:text-white"
                      placeholder="Your Phone Number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Workshop Interest *</label>
                    <select
                      name="workshop"
                      value={workshopFormData.workshop}
                      onChange={handleWorkshopChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600 text-gray-900 dark:text-white"
                    >
                      <option value="">Select Workshop</option>
                      <option value="data-science-masterclass">Data Science Masterclass</option>
                      <option value="web-development-bootcamp">Web Development Bootcamp</option>
                      <option value="machine-learning-workshop">Machine Learning Workshop</option>
                      <option value="full-stack-development">Full Stack Development</option>
                      <option value="python-programming">Python Programming</option>
                      <option value="java-fundamentals">Java Fundamentals</option>
                      <option value="cloud-computing">Cloud Computing</option>
                      <option value="devops-practices">DevOps Practices</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Message</label>
                    <textarea
                      name="message"
                      value={workshopFormData.message}
                      onChange={handleWorkshopChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600 text-gray-900 dark:text-white"
                      placeholder="Any specific questions or requirements?"
                    />
                  </div>
                  
                  {workshopStatus === 'error' && (
                    <div className="flex items-center p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-500 mr-3" />
                      <span className="text-red-700 dark:text-red-300">{workshopErrorMessage}</span>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={workshopStatus === 'loading'}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    {workshopStatus === 'loading' ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Register for Workshop
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}



      {/* Certificate Modal */}
      <CertificateModal
        isOpen={certificateModal.isOpen}
        onClose={closeCertificateModal}
        imageSrc={rishiVyasImage}
        imageAlt="Rishi Vyas Data Analysis Certificate"
      />
    </div>
  );
};

export default Home;